import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  useSecurityDeposits,
  useSecurityDepositDetails,
  useUpdateSecurityDeposit,
} from "services/hooks/rentals";
import { currencyFormatter } from "utilities/currencyFormatter";
import { formatDate } from "utilities/date";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SecurityIcon from "@mui/icons-material/Security";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentIcon from "@mui/icons-material/Payment";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";

const SecurityDeposits = ({ propertyId }) => {
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [updateData, setUpdateData] = useState({});

  const { deposits, summary, isLoadingDeposits, error, refetch } = useSecurityDeposits(propertyId);
  const { deposit: depositDetails, isLoadingDetails } = useSecurityDepositDetails(selectedDeposit);
  const { updateDeposit, isUpdating } = useUpdateSecurityDeposit();

  const handleViewDetails = (depositId) => {
    setSelectedDeposit(depositId);
    setOpenDetails(true);
  };

  const handleEditDeposit = (deposit) => {
    setSelectedDeposit(deposit.id);
    setUpdateData({
      is_paid: deposit.is_paid,
      payment_date: deposit.payment_date || "",
      payment_method: deposit.payment_method || "",
      payment_reference: deposit.payment_reference || "",
      is_refunded: deposit.is_refunded,
      refund_date: deposit.refund_date || "",
      refund_amount: deposit.refund_amount || "",
      refund_notes: deposit.refund_notes || "",
      notes: deposit.notes || "",
    });
    setOpenUpdate(true);
  };

  const handleUpdateSubmit = async () => {
    try {
      await updateDeposit(selectedDeposit, updateData);
      setOpenUpdate(false);
      refetch();
    } catch (error) {
      console.error("Error updating deposit:", error);
    }
  };

  const getPaymentStatusColor = (isPaid, isRefunded) => {
    if (isRefunded) return "info";
    if (isPaid) return "success";
    return "error";
  };

  const getPaymentStatusText = (isPaid, isRefunded) => {
    if (isRefunded) return "Refunded";
    if (isPaid) return "Paid";
    return "Unpaid";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoadingDeposits) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading security deposits. Please try again.
      </Alert>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon />
        Security Deposits
      </Typography>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Deposits
                </Typography>
                <Typography variant="h4">
                  {summary.total_deposits}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {summary.property_name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatCurrency(summary.total_amount)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {summary.landlord_name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Paid Deposits
                </Typography>
                <Typography variant="h4" color="success.main">
                  {summary.paid_deposits}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {summary.unpaid_deposits} unpaid
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Refunded
                </Typography>
                <Typography variant="h4" color="info.main">
                  {summary.refunded_deposits}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Refunded deposits
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Deposits Table */}
      <Paper sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Security Deposits ({deposits?.length || 0})
        </Typography>
        <Divider />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Refund Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deposits?.map((deposit) => (
                <TableRow key={deposit.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {deposit.tenant_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{deposit.unit_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(deposit.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPaymentStatusText(deposit.is_paid, deposit.is_refunded)}
                      color={getPaymentStatusColor(deposit.is_paid, deposit.is_refunded)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {deposit.payment_date ? formatDate(deposit.payment_date) : "N/A"}
                  </TableCell>
                  <TableCell>{deposit.payment_method || "N/A"}</TableCell>
                  <TableCell>
                    {deposit.is_refunded ? (
                      <Chip label="Refunded" color="info" size="small" />
                    ) : (
                      <Chip label="Not Refunded" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(deposit.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Deposit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditDeposit(deposit)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Details Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon />
            Security Deposit Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {isLoadingDetails ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : depositDetails ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Tenant Name</Typography>
                <Typography variant="body1">{depositDetails.tenant_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Unit</Typography>
                <Typography variant="body1">{depositDetails.unit_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Property</Typography>
                <Typography variant="body1">{depositDetails.property_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Landlord</Typography>
                <Typography variant="body1">{depositDetails.landlord_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatCurrency(depositDetails.amount)} {depositDetails.currency}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Deposit Type</Typography>
                <Typography variant="body1">{depositDetails.deposit_type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Status</Typography>
                <Chip
                  label={getPaymentStatusText(depositDetails.is_paid, depositDetails.is_refunded)}
                  color={getPaymentStatusColor(depositDetails.is_paid, depositDetails.is_refunded)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Date</Typography>
                <Typography variant="body1">
                  {depositDetails.payment_date ? formatDate(depositDetails.payment_date) : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Method</Typography>
                <Typography variant="body1">{depositDetails.payment_method || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Payment Reference</Typography>
                <Typography variant="body1">{depositDetails.payment_reference || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Refund Status</Typography>
                <Chip
                  label={depositDetails.is_refunded ? "Refunded" : "Not Refunded"}
                  color={depositDetails.is_refunded ? "info" : "default"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Refund Date</Typography>
                <Typography variant="body1">
                  {depositDetails.refund_date ? formatDate(depositDetails.refund_date) : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Refund Amount</Typography>
                <Typography variant="body1">
                  {depositDetails.refund_amount ? formatCurrency(depositDetails.refund_amount) : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Refund Notes</Typography>
                <Typography variant="body1">{depositDetails.refund_notes || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                <Typography variant="body1">{depositDetails.notes || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                <Typography variant="body1">{formatDate(depositDetails.created_at)}</Typography>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update Dialog */}
      <Dialog open={openUpdate} onClose={() => setOpenUpdate(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Update Security Deposit
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={updateData.is_paid}
                  onChange={(e) => setUpdateData({ ...updateData, is_paid: e.target.value })}
                  label="Payment Status"
                >
                  <MenuItem value={false}>Unpaid</MenuItem>
                  <MenuItem value={true}>Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={updateData.payment_date}
                onChange={(e) => setUpdateData({ ...updateData, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Method"
                value={updateData.payment_method}
                onChange={(e) => setUpdateData({ ...updateData, payment_method: e.target.value })}
                placeholder="e.g., bank transfer, mobile money"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Reference"
                value={updateData.payment_reference}
                onChange={(e) => setUpdateData({ ...updateData, payment_reference: e.target.value })}
                placeholder="Transaction reference"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Refund Status</InputLabel>
                <Select
                  value={updateData.is_refunded}
                  onChange={(e) => setUpdateData({ ...updateData, is_refunded: e.target.value })}
                  label="Refund Status"
                >
                  <MenuItem value={false}>Not Refunded</MenuItem>
                  <MenuItem value={true}>Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Refund Date"
                type="date"
                value={updateData.refund_date}
                onChange={(e) => setUpdateData({ ...updateData, refund_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Refund Amount"
                type="number"
                value={updateData.refund_amount}
                onChange={(e) => setUpdateData({ ...updateData, refund_amount: e.target.value })}
                placeholder="Refund amount"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Refund Notes"
                value={updateData.refund_notes}
                onChange={(e) => setUpdateData({ ...updateData, refund_notes: e.target.value })}
                placeholder="Refund notes"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={updateData.notes}
                onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                placeholder="Additional notes"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUpdate(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateSubmit}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : <MoneyOffIcon />}
          >
            {isUpdating ? "Updating..." : "Update Deposit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityDeposits;
